// The single response handler the envelope exists to make possible.
//
// Every call returns `data` or throws ApiClientError carrying the stable
// `code`. Callers branch on `err.code`, never on the message.

import type {
  InviteLinkPreview,
  JoinByCodeResult,
  PersonalInviteLink,
  ReportRequest,
  ReportResponse,
  ApiResponse,
  ApprovePhotoResponse,
  AuthResponse,
  CloseEventRequest,
  CloseEventResponse,
  ConfirmDayofResponse,
  ContactRequest,
  CreateEventRequest,
  CreateGroupRequest,
  CreateInviteCodeRequest,
  AddTournamentPlayerRequest,
  CreateTournamentRequest,
  CurrentUser,
  SetSeatingRequest,
  CompleteTournamentRequest,
  EndFastRequest,
  ChatConversationState,
  FastHistory,
  FastMemberHistory,
  FastingState,
  LogKetosisRequest,
  MyFastingState,
  SetScoresRequest,
  StartFastRequest,
  SetScoringRequest,
  TournamentDrawResult,
  TournamentState,
  DeleteEventResponse,
  DeletePhotoResponse,
  DeleteSeriesResponse,
  EmailPrefsResponse,
  EmailPrefsSubscriptionRequest,
  EmailPrefsUpdateRequest,
  ErrorCode,
  EventDetail,
  EventSeries,
  EventSummary,
  ForgotRequest,
  ForgotResponse,
  GroupDetail,
  GroupMember,
  GroupSummary,
  InviteCode,
  InviteMemberRequest,
  InviteMemberResponse,
  LeaderboardResponse,
  MaterializeNextResponse,
  MessageResponse,
  RegisterResponse,
  NotificationPrefs,
  PrefsResponse,
  PushSubscribeRequest,
  TokenConfirmResponse,
  UnsubscribeResponse,
  UpdatePrefsRequest,
  NotificationSettingsResponse,
  GroupNotificationSettingsResponse,
  UpdateNotifCellsRequest,
  ChatMessage,
  ChatPage,
  DeleteChatResponse,
  ActivityPage,
  GroupPot,
  PotWinRequest,
  PotWinResponse,
  FeedPage,
  FeedPost,
  ChatReactionActionResponse,
  PutReactionRequest,
  ReactionActionResponse,
  SetReactionMuteRequest,
  Photo,
  PhotoListResponse,
  RegisterRequest,
  RemoveMemberResponse,
  ResetRequest,
  RsvpCounts,
  RsvpRosterResponse,
  RsvpStatus,
  SaveRsvpRequest,
  SaveRsvpResponse,
  SeriesDetail,
  SeriesRequest,
  TransferOwnerResponse,
  UpdateEventRequest,
  UpdateGroupRequest,
  UploadPhotoResponse,
  VerifyRequest,
  MyEventSummary,
  AdminInviteCode,
  AdminInviteCodesResponse,
  AdminCreateInviteCodeRequest,
  AdminGroupRow,
  AdminGroupStatus,
  AdminGroupOrder,
  AdminUserRow,
  AdminUserStatus,
  AdminUserOrder,
  AdminUpdateUserRequest,
  MasqueradeMode,
  MasqueradeState,
  UpdateProfileRequest,
  ChangePasswordRequest,
  ChangePasswordResponse,
  DeleteAccountRequest,
  DeleteAccountResponse,
  StartPhoneVerificationRequest,
  BlockListResponse,
  BlockStatusResponse,
  StartEmailChangeResponse,
  UserProfile,
  StartPhoneVerificationResponse,
  VerifyPhoneRequest,
  CreateCategoryRequestPayload,
  AdminCategoryRequestRow,
  CategoryRequestStatus,
  ContactsPageDto,
  ContactDetailDto,
  ContactListDto,
  BulkResultDto,
  BulkTieMode,
  ContactFacetsDto,
  ContactLayer,
  ConnectionsDto,
  ContactSortKey,
  HouseholdDto,
  SortDirection,
  SocialTie,
  PublicGroupSummary,
  JoinGroupResult,
  GroupJoinRequest,
} from '@msc/shared';
import {
  clearTokens,
  getAccessToken,
  getMasquerade,
  getRefreshToken,
  hasFreshAccessToken,
  resyncRefreshToken,
  setAccessToken,
  setMasquerade,
  setRefreshToken,
} from './tokens.js';
import { stopRealtime } from '../realtime.js';
// Same shape of cycle as stopRealtime above, and for the same reason: an
// identity change has to invalidate state other modules cached about the
// previous identity.
import { forgetViewerZone } from '../viewerZone.js';
import { apiOrigin } from '../apiOrigin.js';
import { clientId } from './clientId.js';

// Absolute on native, same-origin on the web. See `apiOrigin.ts` for why the
// shell CANNOT use a relative path here.
const PREFIX = '/api/v1';
function base(): string {
  return apiOrigin() + PREFIX;
}

export class ApiClientError extends Error {
  readonly code: ErrorCode | 'NETWORK';
  readonly status: number;
  readonly details: unknown;

  constructor(status: number, code: ErrorCode | 'NETWORK', message: string, details?: unknown) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Internal: prevents refresh recursion. */
  skipAuth?: boolean;
}

async function raw<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  // On EVERY request rather than opt-in per call, because a rule a call site
  // can forget is a rule that gets forgotten. Only the tournament routes read
  // it today; see `clientId.ts` for what it is for and what it is not.
  headers['X-Client-Id'] = clientId();

  const token = getAccessToken();
  if (token && !opts.skipAuth) headers['Authorization'] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(base() + path, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  } catch {
    throw new ApiClientError(0, 'NETWORK', 'Cannot reach the server');
  }

  let payload: ApiResponse<T>;
  try {
    payload = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError(res.status, 'NETWORK', 'Malformed response from server');
  }

  if (!payload.ok) {
    throw new ApiClientError(res.status, payload.error.code, payload.error.message, payload.error.details);
  }
  return payload.data;
}

/**
 * The in-flight rotation, shared by every concurrent caller.
 *
 * ⚠ THIS IS NOT AN OPTIMISATION. Refresh tokens are single-use: the server
 * deletes the presented row and mints a replacement, and a token that matches no
 * row is treated as THEFT — it revokes the user's entire token family on every
 * device.
 *
 * So two concurrent refreshes with the same token are indistinguishable from a
 * stolen one. The first wins; the rest present an already-spent token and destroy
 * the session that was just created. Any view issuing parallel requests triggers
 * it — the home page fires three — and it only shows up after the access token
 * has expired, so it reads as "I stepped away for 20 minutes and got logged out".
 *
 * Single-flighting here is what makes rotation and theft detection able to
 * coexist. Do not remove it, and do not let a caller bypass it by calling the
 * inner function.
 */
let refreshInFlight: Promise<CurrentUser | null> | null = null;

/**
 * The same exclusion, one scope up: across TABS.
 *
 * `refreshInFlight` is a module-level variable, so it only ever covered the tab
 * it lives in. With "stay logged in" the refresh token sits in localStorage,
 * shared by every tab on the origin — so two tabs whose access tokens expire
 * together both present the SAME token, the second one matches no row, and the
 * server correctly reads that as theft and revokes the family. Every tab, and
 * every other device, is signed out. It surfaces as random logouts with nothing
 * in the logs, because nothing failed.
 *
 * Web Locks serialises the rotation across tabs. Re-reading the token INSIDE the
 * lock is the other half and is not optional: the loser of the race wakes up
 * holding the mirror it hydrated at boot, which is precisely the spent token.
 * Its own rotation must start from whatever the winner just wrote.
 *
 * Falls through to a plain rotation where there is no lock manager — native runs
 * one webview, so it has no second tab to race.
 */
async function rotateExclusively(): Promise<CurrentUser | null> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return rotateOnce();
  return await locks.request('msc.refresh', async () => {
    await resyncRefreshToken();
    return rotateOnce();
  });
}

/** Exchange the stored refresh token for a new access token. */
export function refreshSession(): Promise<CurrentUser | null> {
  // Cleared in `finally` rather than after `then`, so a rejected rotation does
  // not pin a permanently-failing promise for the rest of the session.
  refreshInFlight ??= rotateExclusively().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/**
 * Did the SERVER actually reject the token, or did we just fail to reach it?
 *
 * Only the first may end the session. A network failure is INDETERMINATE in
 * exactly the way a mail-send timeout is (see CLAUDE.md): the refresh token is
 * very likely still valid and the server merely unreachable — an API restart, a
 * proxy blip, a phone passing through a dead zone. Clearing on one of those
 * destroys a perfectly good 30-day session and forces a re-login for a hiccup
 * that lasted a second.
 *
 * `NETWORK` covers both an unreachable server (status 0) and a non-JSON reply
 * from something sitting in front of it, such as a proxy's own 502 page. A 5xx
 * is the server failing, not the token being refused, so it is indeterminate
 * too. That leaves a 401 or 403 carrying a real error envelope as the only
 * definitive answer.
 */
function isDefinitiveRejection(err: unknown): boolean {
  if (!(err instanceof ApiClientError)) return false;
  if (err.code === 'NETWORK') return false;
  // 401 and 403 only: the server looked at the token (or the account) and said
  // no. A 429 or a 400 says nothing about the token, and clearing on one would
  // throw away a session the server still honours.
  return err.status === 401 || err.status === 403;
}

async function rotateOnce(): Promise<CurrentUser | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  // A masquerade lives only in the access token, which is the thing that just
  // expired — so the refresh has to say who we were acting as, or the admin
  // silently pops back into their own account with the banner gone. The server
  // re-verifies every part of it; nothing here is taken on trust.
  const masq = getMasquerade();

  try {
    const data = await raw<AuthResponse>('/auth/refresh', {
      method: 'POST',
      body: masq
        ? { refreshToken, masquerade: { userId: masq.subjectId, mode: masq.mode } }
        : { refreshToken },
      skipAuth: true,
    });
    setAccessToken(data.accessToken, data.expiresIn);
    setRefreshToken(data.refreshToken);
    // The server decides whether the masquerade survived. If it refused — the
    // admin was demoted, or the subject was promoted — the response comes back
    // without it and the banner correctly disappears.
    setMasquerade(data.masquerade ?? null);
    // A refusal is an identity change nobody clicked: the effective user
    // reverted to the admin mid-session, so a zone cached for the subject is
    // now somebody else's.
    if (masq && !data.masquerade) forgetViewerZone();
    return data.user;
  } catch (err) {
    // Keep the token unless the server definitively refused it. Returning null
    // still signs the user out of THIS render, which callers already handle by
    // propagating their original error — but the token survives, so the next
    // call or a reload silently re-authenticates instead of dumping someone at
    // the login screen because the API was restarting.
    if (isDefinitiveRejection(err)) {
      clearTokens();
      // The session is gone, so whatever zone is cached belongs to a user who is
      // no longer signed in. Keeps the invariant simple: tokens cleared, zone
      // forgotten, every time.
      forgetViewerZone();
    }
    return null;
  }
}

/**
 * A multipart POST, for file uploads.
 *
 * Separate from `raw()` because FormData must NOT get a Content-Type header set
 * by hand: the browser has to add its own, including the generated boundary, and
 * supplying one without a boundary makes the body unparseable. Everything else —
 * the envelope, the error codes, the token refresh — is identical.
 */
async function rawUpload<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  headers['X-Client-Id'] = clientId();
  const token = getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(base() + path, { method: 'POST', headers, body: form });
  } catch {
    throw new ApiClientError(0, 'NETWORK', 'Cannot reach the server');
  }

  let payload: ApiResponse<T>;
  try {
    payload = (await res.json()) as ApiResponse<T>;
  } catch {
    // A 413 from the reverse proxy arrives as HTML, not our envelope. Say
    // something useful rather than "malformed response": the app's own 10 MB
    // check should have caught it first, so this means the proxy limit is set
    // too low (see the client_max_body_size note in docs/architecture.md).
    if (res.status === 413) {
      throw new ApiClientError(413, 'FILE_TOO_LARGE', 'That file is too large to upload.');
    }
    throw new ApiClientError(res.status, 'NETWORK', 'Malformed response from server');
  }

  if (!payload.ok) {
    throw new ApiClientError(res.status, payload.error.code, payload.error.message, payload.error.details);
  }
  return payload.data;
}

/** Upload with the same refresh-then-retry behaviour as `api()`. */
async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  if (!hasFreshAccessToken() && getRefreshToken()) await refreshSession();

  try {
    return await rawUpload<T>(path, form);
  } catch (err) {
    const is401 = err instanceof ApiClientError && err.status === 401;
    if (!is401 || !getRefreshToken()) throw err;
    const user = await refreshSession();
    if (!user) throw err;
    // FormData is single-use in some engines once consumed by fetch; it is not
    // here because fetch clones the body, and a retry after refresh is the only
    // retry that ever happens.
    return rawUpload<T>(path, form);
  }
}

/**
 * Authenticated request. Refreshes proactively when the access token is stale,
 * and reactively once on a 401 in case the server disagrees about expiry.
 */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  if (!hasFreshAccessToken() && getRefreshToken()) {
    await refreshSession();
  }

  try {
    return await raw<T>(path, opts);
  } catch (err) {
    const is401 = err instanceof ApiClientError && err.status === 401;
    if (!is401 || !getRefreshToken()) throw err;

    const user = await refreshSession();
    if (!user) throw err;
    return raw<T>(path, opts);
  }
}

// --- Endpoints ------------------------------------------------------------

/**
 * `stayLoggedIn` is a CLIENT-side storage choice, not a server concern — the
 * server issues the same rotating 30-day token either way and does not care where
 * it is kept. Checked persists it across a browser or app restart; unchecked
 * keeps it for the tab only.
 */
export async function login(
  username: string,
  password: string,
  stayLoggedIn = true,
): Promise<CurrentUser> {
  const data = await raw<AuthResponse>('/auth/login', {
    method: 'POST',
    body: { username, password },
    skipAuth: true,
  });
  setAccessToken(data.accessToken, data.expiresIn);
  setRefreshToken(data.refreshToken, stayLoggedIn);
  // The anonymous visit that sent the user here already resolved a display zone
  // and cached it: /me answered 401, so the cache holds the BROWSER zone. Login
  // is a pushState render, not a page load, so that cache would otherwise
  // outlive the sign-in and show every screen the wrong zone until a reload.
  forgetViewerZone();
  return data.user;
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  clearTokens();
  // Close the realtime socket too, or it keeps reconnecting with a dead token
  // for the rest of the page's life.
  stopRealtime();
  // The next user to sign in on this tab is not necessarily this one.
  forgetViewerZone();
  if (!refreshToken) return;
  try {
    await raw('/auth/logout', { method: 'POST', body: { refreshToken }, skipAuth: true });
  } catch {
    /* the local tokens are already gone — logout never fails the user */
  }
}

export function fetchMe(): Promise<CurrentUser> {
  return api<CurrentUser>('/me');
}

// --- The account page ------------------------------------------------------

/**
 * Save profile fields. Only the keys present are written, so each section of the
 * account page saves itself without blanking the others.
 *
 * Drops the viewer-zone cache, because `timeZone` is the FIRST link in the chain
 * `viewerZone()` resolves — every rendered date would otherwise keep using the
 * old zone until a reload. `forgetViewerZone()` was written for exactly this
 * call, with a comment saying so, and had no caller until now.
 */
export async function updateProfile(body: UpdateProfileRequest): Promise<CurrentUser> {
  const user = await api<CurrentUser>('/me', { method: 'PATCH', body });
  if (body.timeZone !== undefined) forgetViewerZone();
  return user;
}

/**
 * Change the password. Signs the user out EVERYWHERE, this device included, so
 * the local tokens are dropped here rather than left to fail on the next call.
 *
 * See shared/src/auth.ts on ChangePasswordResponse for why the current device is
 * not spared: the alternative logs the user out later, unpredictably.
 */
export async function changePassword(body: ChangePasswordRequest): Promise<ChangePasswordResponse> {
  const result = await api<ChangePasswordResponse>('/me/password', { method: 'POST', body });
  clearTokens();
  stopRealtime();
  forgetViewerZone();
  return result;
}

/**
 * Close the account. App Store Guideline 5.1.1(v) -- see docs/plan-ios-ship.md.
 *
 * Signs the user out everywhere by definition, so the local tokens are dropped
 * here exactly as `changePassword()` does. Not dropping them would leave the SPA
 * holding a token for an account that no longer resolves, and the next call
 * would fail as an authentication error rather than as "you deleted this".
 *
 * A 409 OWNS_GROUPS is the one recoverable failure and it throws with
 * `details.groups`, so the caller keeps its tokens and can offer the transfer.
 */
export async function deleteAccount(
  body: DeleteAccountRequest,
): Promise<DeleteAccountResponse> {
  const result = await api<DeleteAccountResponse>('/me', { method: 'DELETE', body });
  clearTokens();
  stopRealtime();
  forgetViewerZone();
  return result;
}

/** Start verifying a phone number. Texts a code; stores nothing on `phone` yet. */
export function startPhoneVerification(
  body: StartPhoneVerificationRequest,
): Promise<StartPhoneVerificationResponse> {
  return api<StartPhoneVerificationResponse>('/me/phone', { method: 'POST', body });
}

/** Finish it. On success the pending number becomes the account's real one. */
export function verifyPhone(body: VerifyPhoneRequest): Promise<CurrentUser> {
  return api<CurrentUser>('/me/phone/verify', { method: 'POST', body });
}

export function removePhone(): Promise<CurrentUser> {
  return api<CurrentUser>('/me/phone', { method: 'DELETE' });
}

/**
 * Upload an avatar. The server derives the header thumbnail from this file, so
 * only one image is sent.
 *
 * The field name must be `avatar` — the server drains every other file part.
 */
export function uploadAvatar(file: File): Promise<CurrentUser> {
  const form = new FormData();
  form.append('avatar', file);
  return apiUpload<CurrentUser>('/me/avatar', form);
}

/**
 * Block a member. Mutual in effect: neither sees the other's posts, chat or
 * photos afterwards. Event announcement cards are unaffected.
 */
export function blockUser(userId: number): Promise<BlockStatusResponse> {
  return api<BlockStatusResponse>(`/users/${userId}/block`, { method: 'POST' });
}

/** Lift a block. Only the blocker can; scoped server-side to their own rows. */
export function unblockUser(userId: number): Promise<BlockStatusResponse> {
  return api<BlockStatusResponse>(`/users/${userId}/block`, { method: 'DELETE' });
}

/** Who this account has blocked. There is no way to see who blocked you. */
export function fetchBlocked(): Promise<BlockListResponse> {
  return api<BlockListResponse>('/me/blocks');
}

/**
 * Another user's profile, as this viewer is allowed to see it.
 *
 * The server decides field by field what comes back -- see `projectProfile()`.
 * A withheld field arrives as '' rather than missing, so there is nothing to
 * infer here and no permission logic on the client.
 */
export function fetchUserProfile(userId: number): Promise<UserProfile> {
  return api<UserProfile>(`/users/${userId}/profile`);
}

/**
 * Ask for an email change. Sends a confirmation link to the NEW address; nothing
 * changes until that link is followed, and the current address keeps working.
 */
export function startEmailChange(email: string): Promise<StartEmailChangeResponse> {
  return api<StartEmailChangeResponse>('/me/email', { method: 'POST', body: { email } });
}

/** Finish it, with the token out of the emailed link. */
export function confirmEmailChange(token: string): Promise<CurrentUser> {
  return api<CurrentUser>('/me/email/confirm', { method: 'POST', body: { token } });
}

export function removeAvatar(): Promise<CurrentUser> {
  return api<CurrentUser>('/me/avatar', { method: 'DELETE' });
}

export function register(body: RegisterRequest): Promise<RegisterResponse> {
  return raw<RegisterResponse>('/auth/register', { method: 'POST', body, skipAuth: true });
}

/** /contact — public, no session. See server/src/contact/routes.ts. */
export function sendContact(body: ContactRequest): Promise<MessageResponse> {
  return raw<MessageResponse>('/contact', { method: 'POST', body, skipAuth: true });
}

/** Accept an invite: sets the password, activates the account, and auto-logs-in. */
export async function verifyInvite(body: VerifyRequest): Promise<CurrentUser> {
  const data = await raw<AuthResponse>('/auth/verify', { method: 'POST', body, skipAuth: true });
  setAccessToken(data.accessToken, data.expiresIn);
  setRefreshToken(data.refreshToken);
  // Verify is where the user PICKS their zone, so a stale anonymous cache here
  // would ignore the choice they just made.
  forgetViewerZone();
  return data.user;
}

export function forgotPassword(body: ForgotRequest): Promise<ForgotResponse> {
  return raw<ForgotResponse>('/auth/forgot', { method: 'POST', body, skipAuth: true });
}

/** Finish a password reset. No auto-login — the user logs in fresh afterwards. */
export function resetPassword(body: ResetRequest): Promise<MessageResponse> {
  return raw<MessageResponse>('/auth/reset', { method: 'POST', body, skipAuth: true });
}

/**
 * Best-effort lookup of the email address behind a verify/reset code, purely
 * to prefill the hidden username-hint field on those forms so Chrome's
 * password manager has a real address to save against. Never throws --
 * a null address just means the hint field stays empty, which is the
 * (correct, if less convenient) fallback behavior.
 */
export async function inviteAddress(code: string): Promise<string | null> {
  try {
    const { address } = await raw<{ address: string | null }>(
      `/auth/invite-address?code=${encodeURIComponent(code)}`,
      { skipAuth: true },
    );
    return address;
  } catch {
    return null;
  }
}

export interface ResetContext {
  /** The address the link was sent to, for the password manager's username field. */
  address: string | null;
  /** True only when this reset will also ACTIVATE a never-used account. */
  needsName: boolean;
}

export async function resetAddress(code: string): Promise<ResetContext> {
  try {
    return await raw<ResetContext>(`/auth/reset-address?code=${encodeURIComponent(code)}`, {
      skipAuth: true,
    });
  } catch {
    // Same failure shape as before: unknown address, and do not ask for a name.
    // Names are optional on the reset path (the server COALESCEs them), so the
    // safe default when this call fails is the quiet form, not the noisy one.
    return { address: null, needsName: false };
  }
}

// --- Groups ---------------------------------------------------------------

export function listGroups(): Promise<GroupSummary[]> {
  return api<GroupSummary[]>('/groups');
}

export function createGroup(body: CreateGroupRequest): Promise<GroupDetail> {
  return api<GroupDetail>('/groups', { method: 'POST', body });
}

export function fetchGroup(groupId: number): Promise<GroupDetail> {
  return api<GroupDetail>(`/groups/${groupId}`);
}

/** "Suggest a new event type" — see docs/domain.md, "Group categories". */
export function requestCategory(body: CreateCategoryRequestPayload): Promise<MessageResponse> {
  return api<MessageResponse>('/category-requests', { method: 'POST', body });
}

export function adminListCategoryRequests(): Promise<AdminCategoryRequestRow[]> {
  return api<AdminCategoryRequestRow[]>('/admin/category-requests');
}

export function adminSetCategoryRequestStatus(
  requestId: number,
  status: CategoryRequestStatus,
): Promise<{ updated: true }> {
  return api<{ updated: true }>(`/admin/category-requests/${requestId}`, {
    method: 'PATCH',
    body: { status },
  });
}

export function updateGroup(groupId: number, body: UpdateGroupRequest): Promise<GroupDetail> {
  return api<GroupDetail>(`/groups/${groupId}`, { method: 'PATCH', body });
}

/**
 * Upload the group's info-panel photo.
 *
 * The field name must be `photo` -- the server drains every other file part.
 * Multipart, so it is its own endpoint rather than a field on `updateGroup`:
 * the JSON body parser never sees it.
 */
export function uploadGroupPhoto(groupId: number, file: File): Promise<GroupDetail> {
  const form = new FormData();
  form.append('photo', file);
  return apiUpload<GroupDetail>(`/groups/${groupId}/photo`, form);
}

export function removeGroupPhoto(groupId: number): Promise<GroupDetail> {
  return api<GroupDetail>(`/groups/${groupId}/photo`, { method: 'DELETE' });
}

/**
 * Search public groups. A term under 2 characters is answered with [] by the
 * server rather than the whole site; the caller does not need to guard it.
 */
export function searchGroups(term: string): Promise<PublicGroupSummary[]> {
  return api<PublicGroupSummary[]>(`/groups/search?q=${encodeURIComponent(term)}`);
}

/**
 * Ask to join a public group, or walk straight in.
 *
 * ⚠ Branch on `result.outcome`, never on the group's `joinPolicy`. The policy
 * can change between the search result being rendered and the button being
 * pressed, and the server reports what actually happened.
 */
export function joinGroup(groupId: number, message?: string): Promise<JoinGroupResult> {
  return api<JoinGroupResult>(`/groups/${groupId}/join`, {
    method: 'POST',
    body: message ? { message } : {},
  });
}

export function listJoinRequests(groupId: number): Promise<GroupJoinRequest[]> {
  return api<GroupJoinRequest[]>(`/groups/${groupId}/join-requests`);
}

/** Both decisions return the REMAINING queue, so the caller re-renders from it. */
export function approveJoinRequest(
  groupId: number,
  requestId: number,
): Promise<GroupJoinRequest[]> {
  return api<GroupJoinRequest[]>(`/groups/${groupId}/join-requests/${requestId}/approve`, {
    method: 'POST',
    body: {},
  });
}

export function denyJoinRequest(groupId: number, requestId: number): Promise<GroupJoinRequest[]> {
  return api<GroupJoinRequest[]>(`/groups/${groupId}/join-requests/${requestId}/deny`, {
    method: 'POST',
    body: {},
  });
}

export function deleteGroup(groupId: number): Promise<{ groupId: number; deleted: boolean }> {
  return api(`/groups/${groupId}`, { method: 'DELETE' });
}

export function listMembers(groupId: number): Promise<GroupMember[]> {
  return api<GroupMember[]>(`/groups/${groupId}/members`);
}

export function updateMemberRole(
  groupId: number,
  userId: number,
  role: number,
): Promise<{ role: number; roleLabel: string; changed: boolean }> {
  return api(`/groups/${groupId}/members/${userId}`, { method: 'PATCH', body: { role } });
}

/**
 * Hand the group to another member. Owner only, and irreversible from the
 * caller's side: they come back as Admin and only the new owner can undo it.
 */
export function transferOwnership(
  groupId: number,
  userId: number,
): Promise<TransferOwnerResponse> {
  return api<TransferOwnerResponse>(`/groups/${groupId}/transfer-owner`, {
    method: 'POST',
    body: { userId },
  });
}

export function removeMember(groupId: number, userId: number): Promise<RemoveMemberResponse> {
  return api<RemoveMemberResponse>(`/groups/${groupId}/members/${userId}`, { method: 'DELETE' });
}

export function inviteMember(
  groupId: number,
  body: InviteMemberRequest,
): Promise<InviteMemberResponse> {
  return api<InviteMemberResponse>(`/groups/${groupId}/invites`, { method: 'POST', body });
}

/** MY invite link for this group: created the first time, the same one after. */
export function getMyInviteLink(groupId: number): Promise<PersonalInviteLink> {
  return api<PersonalInviteLink>(`/groups/${groupId}/my-invite-link`, { method: 'POST' });
}

/** What the join page may show before anyone has signed in. */
export function previewInviteLink(code: string): Promise<InviteLinkPreview> {
  return api<InviteLinkPreview>(`/invite-links/${encodeURIComponent(code)}`);
}

/** An existing, signed-in account joins by link. Never sets a parent. */
export function joinByInviteLink(code: string): Promise<JoinByCodeResult> {
  return api<JoinByCodeResult>(`/invite-links/${encodeURIComponent(code)}/join`, { method: 'POST' });
}

export function listInviteCodes(groupId: number): Promise<InviteCode[]> {
  return api<InviteCode[]>(`/groups/${groupId}/invite-codes`);
}

export function createInviteCode(
  groupId: number,
  body: CreateInviteCodeRequest,
): Promise<InviteCode> {
  return api<InviteCode>(`/groups/${groupId}/invite-codes`, { method: 'POST', body });
}

/** Revoke or restore. Codes are never hard deleted. */
export function setInviteCodeActive(
  groupId: number,
  codeId: number,
  isActive: boolean,
): Promise<InviteCode> {
  return api<InviteCode>(`/groups/${groupId}/invite-codes/${codeId}`, {
    method: 'PATCH',
    body: { isActive },
  });
}

// --- Events ---------------------------------------------------------------

export interface ListEventsOptions {
  /** Drop anything before site-local today. */
  upcoming?: boolean;
  /** False drops closed events — the event box wants that. Defaults to true. */
  includeClosed?: boolean;
  /** Inclusive 'yyyy-MM-dd' window. The calendar's month. */
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export function listEvents(groupId: number, opts: ListEventsOptions = {}): Promise<EventSummary[]> {
  const q = new URLSearchParams();
  if (opts.upcoming) q.set('upcoming', '1');
  if (opts.includeClosed === false) q.set('includeClosed', '0');
  if (opts.from) q.set('from', opts.from);
  if (opts.to) q.set('to', opts.to);
  if (opts.limit !== undefined) q.set('limit', String(opts.limit));
  if (opts.offset !== undefined) q.set('offset', String(opts.offset));
  const qs = q.toString();
  return api<EventSummary[]>(`/groups/${groupId}/events${qs ? `?${qs}` : ''}`);
}

/**
 * Events across every group the caller belongs to, newest-first by date.
 *
 * One request, not one per group: the home page and the site-wide views would
 * otherwise fan out over `listGroups()` and merge on the client, which is an
 * N+1 that grows with every group joined.
 */
export function listMyEvents(opts: ListEventsOptions = {}): Promise<MyEventSummary[]> {
  const q = new URLSearchParams();
  if (opts.upcoming) q.set('upcoming', '1');
  if (opts.includeClosed === false) q.set('includeClosed', '0');
  if (opts.from) q.set('from', opts.from);
  if (opts.to) q.set('to', opts.to);
  if (opts.limit !== undefined) q.set('limit', String(opts.limit));
  if (opts.offset !== undefined) q.set('offset', String(opts.offset));
  const qs = q.toString();
  return api<MyEventSummary[]>(`/me/events${qs ? `?${qs}` : ''}`);
}

/**
 * Create an event. This ANNOUNCES it synchronously — every member is emailed
 * before the promise resolves. Never call it against a group with real contacts
 * from a test.
 */
export function createEvent(groupId: number, body: CreateEventRequest): Promise<EventDetail> {
  return api<EventDetail>(`/groups/${groupId}/events`, { method: 'POST', body });
}

export function fetchEvent(eventId: number): Promise<EventDetail> {
  return api<EventDetail>(`/events/${eventId}`);
}

export function updateEvent(eventId: number, body: UpdateEventRequest): Promise<EventDetail> {
  return api<EventDetail>(`/events/${eventId}`, { method: 'PATCH', body });
}

export function deleteEvent(eventId: number): Promise<DeleteEventResponse> {
  return api<DeleteEventResponse>(`/events/${eventId}`, { method: 'DELETE' });
}

/** A second close returns 409 ALREADY_CLOSED — branch on the code, not the text. */
export function closeEvent(eventId: number, body: CloseEventRequest): Promise<CloseEventResponse> {
  return api<CloseEventResponse>(`/events/${eventId}/close`, { method: 'POST', body });
}

/** `year` may be a year, or 'all' for all-time. */
export function fetchLeaderboard(
  groupId: number,
  year?: number | 'all',
): Promise<LeaderboardResponse> {
  const qs = year === undefined ? '' : `?year=${year}`;
  return api<LeaderboardResponse>(`/groups/${groupId}/leaderboard${qs}`);
}

// --- RSVPs ----------------------------------------------------------------

/**
 * Save the caller's RSVP. PUT because it replaces one resource idempotently:
 * sending the same status twice is indistinguishable from sending it once.
 *
 * The response carries fresh counts, so a caller never needs a second request to
 * re-render, and `hooksFired` so the UI can acknowledge "Game on!" the moment it
 * happens. Over the cap throws EVENT_FULL; a closed event throws EVENT_CLOSED.
 */
export function saveRsvp(eventId: number, status: RsvpStatus): Promise<SaveRsvpResponse> {
  return api<SaveRsvpResponse>(`/events/${eventId}/rsvp`, {
    method: 'PUT',
    body: { status } satisfies SaveRsvpRequest,
  });
}

/**
 * Day-of "still in?" confirmation. Never changes `status` — a different column
 * entirely. Idempotent: a repeat call returns the original `confirmedDate`.
 * Throws RSVP_NOT_YES without a yes RSVP, EVENT_ENDED after the event's end.
 */
export function confirmDayof(eventId: number): Promise<ConfirmDayofResponse> {
  return api<ConfirmDayofResponse>(`/events/${eventId}/rsvp/confirm`, { method: 'POST' });
}

/** The headcount alone, without the event's winners and notes. */
export function fetchRsvpCounts(eventId: number): Promise<RsvpCounts> {
  return api<RsvpCounts>(`/events/${eventId}/rsvp-counts`);
}

export interface RsvpRosterOptions {
  status?: RsvpStatus;
  /** True narrows to day-of confirmed yeses. */
  confirmedOnly?: boolean;
}

export function listRsvps(
  eventId: number,
  opts: RsvpRosterOptions = {},
): Promise<RsvpRosterResponse> {
  const q = new URLSearchParams();
  if (opts.status) q.set('status', opts.status);
  if (opts.confirmedOnly) q.set('confirmed', '1');
  const qs = q.toString();
  return api<RsvpRosterResponse>(`/events/${eventId}/rsvps${qs ? `?${qs}` : ''}`);
}

// --- Photos ---------------------------------------------------------------

export interface UploadPhotoOptions {
  /** Shown under the gallery tile. Stored on the photo row. */
  caption?: string;
  /**
   * Feed-composer text. When present the carrying post has a body, which means
   * deleting the photo later leaves the post standing.
   */
  body?: string;
}

/**
 * Upload one photo. multipart/form-data, so this bypasses the JSON `api()`
 * helper — but it must still refresh a stale token and still speak the same
 * envelope, hence `apiUpload` rather than a bare fetch.
 *
 * Distinct failures the caller can branch on: FILE_TOO_LARGE, UNSUPPORTED_MEDIA,
 * NO_FILE, UPLOAD_FAILED.
 */
export function uploadPhoto(
  groupId: number,
  file: File,
  opts: UploadPhotoOptions = {},
): Promise<UploadPhotoResponse> {
  const form = new FormData();
  // The field name must be `photo` — the server drains every other file part.
  form.append('photo', file);
  if (opts.caption) form.append('caption', opts.caption);
  if (opts.body) form.append('body', opts.body);
  return apiUpload<UploadPhotoResponse>(`/groups/${groupId}/photos`, form);
}

export interface ListPhotosOptions {
  eventId?: number;
  /** Moderators only; ignored for anyone who cannot see pending photos. */
  pendingOnly?: boolean;
  limit?: number;
  offset?: number;
}

export function listPhotos(
  groupId: number,
  opts: ListPhotosOptions = {},
): Promise<PhotoListResponse> {
  const q = new URLSearchParams();
  if (opts.eventId !== undefined) q.set('eventId', String(opts.eventId));
  if (opts.pendingOnly) q.set('pending', '1');
  if (opts.limit !== undefined) q.set('limit', String(opts.limit));
  if (opts.offset !== undefined) q.set('offset', String(opts.offset));
  const qs = q.toString();
  return api<PhotoListResponse>(`/groups/${groupId}/photos${qs ? `?${qs}` : ''}`);
}

/** Moderator and above. Idempotent. */
export function approvePhoto(photoId: number): Promise<ApprovePhotoResponse> {
  return api<ApprovePhotoResponse>(`/photos/${photoId}/approve`, { method: 'POST' });
}

/** Uploader or admin. An empty string clears the caption. */
export function updatePhotoCaption(photoId: number, caption: string): Promise<Photo> {
  return api<Photo>(`/photos/${photoId}`, { method: 'PATCH', body: { caption } });
}

/** Soft delete. Uploader or admin. */
export function deletePhoto(photoId: number): Promise<DeletePhotoResponse> {
  return api<DeletePhotoResponse>(`/photos/${photoId}`, { method: 'DELETE' });
}

// --- Event series ---------------------------------------------------------

export function listSeries(groupId: number): Promise<EventSeries[]> {
  return api<EventSeries[]>(`/groups/${groupId}/series`);
}

export function fetchSeries(seriesId: number): Promise<SeriesDetail> {
  return api<SeriesDetail>(`/series/${seriesId}`);
}

/**
 * Create a series. Generates NO occurrences — they materialize when their trigger
 * fires, or via createNextOccurrence(). The response carries the upcoming-date
 * preview, which is the only feedback a lazy model gives the organizer.
 */
export function createSeries(groupId: number, body: SeriesRequest): Promise<SeriesDetail> {
  return api<SeriesDetail>(`/groups/${groupId}/series`, { method: 'POST', body });
}

/** Edits the template only. Existing occurrences are never rewritten. */
export function updateSeries(seriesId: number, body: SeriesRequest): Promise<SeriesDetail> {
  return api<SeriesDetail>(`/series/${seriesId}`, { method: 'PATCH', body });
}

/**
 * Soft-delete a series. By default this only stops future generation and KEEPS
 * occurrences already on the calendar with their RSVPs. Pass true to also remove
 * upcoming ones; past occurrences are never touched.
 */
export function deleteSeries(
  seriesId: number,
  deleteOccurrences = false,
): Promise<DeleteSeriesResponse> {
  const qs = deleteOccurrences ? '?deleteOccurrences=1' : '';
  return api<DeleteSeriesResponse>(`/series/${seriesId}${qs}`, { method: 'DELETE' });
}

/**
 * "Create next occurrence now" — creates it AND announces it immediately, a
 * deliberate override of the schedule. Throws CONFLICT when there is nothing
 * left to create.
 */
export function createNextOccurrence(seriesId: number): Promise<MaterializeNextResponse> {
  return api<MaterializeNextResponse>(`/series/${seriesId}/materialize-next`, { method: 'POST' });
}

// --- Notifications --------------------------------------------------------

export function fetchNotificationSettings(): Promise<NotificationSettingsResponse> {
  return api<NotificationSettingsResponse>('/me/notification-settings');
}

/** Change cells of your own defaults. Returns the whole settings payload. */
export function updateNotificationDefaults(
  body: UpdateNotifCellsRequest,
): Promise<NotificationSettingsResponse> {
  return api<NotificationSettingsResponse>('/me/notification-settings/defaults', {
    method: 'PATCH',
    body,
  });
}

/** Reset your own defaults to the standard ones. Group overrides are kept. */
export function resetNotificationDefaults(): Promise<NotificationSettingsResponse> {
  return api<NotificationSettingsResponse>('/me/notification-settings/defaults', {
    method: 'DELETE',
  });
}

export function fetchGroupNotificationSettings(
  groupId: number,
): Promise<GroupNotificationSettingsResponse> {
  return api<GroupNotificationSettingsResponse>(`/groups/${groupId}/notification-settings`);
}

export function updateGroupNotificationSettings(
  groupId: number,
  body: UpdateNotifCellsRequest,
): Promise<GroupNotificationSettingsResponse> {
  return api<GroupNotificationSettingsResponse>(`/groups/${groupId}/notification-settings`, {
    method: 'PATCH',
    body,
  });
}

/** "Reset to default": the group follows your defaults again. */
export function resetGroupNotificationSettings(
  groupId: number,
): Promise<GroupNotificationSettingsResponse> {
  return api<GroupNotificationSettingsResponse>(`/groups/${groupId}/notification-settings`, {
    method: 'DELETE',
  });
}

/**
 * Turn notification email back on for the signed-in user's own address.
 *
 * Takes no address, deliberately — the server resolves it from the session. See
 * `POST /me/email/resubscribe`. Returns the whole prefs payload so the caller
 * re-renders from one response.
 */
export function resubscribeOwnEmail(): Promise<PrefsResponse> {
  return api<PrefsResponse>('/me/email/resubscribe', { method: 'POST' });
}

// --- Tournaments (Phase 1) -------------------------------------------------
//
// Every call is keyed by eventId and every one returns the WHOLE
// `TournamentState`, so a caller re-renders from one response and never has to
// issue a second fetch to discover what changed. Same shape as the prefs
// endpoints.

const tPath = (eventId: number) => `/events/${eventId}/tournament`;

// --- Fasting (Phase 1) -----------------------------------------------------
//
// Every call returns the whole `FastingState`, so one response refreshes the
// screen -- the same shape as the tournament calls, and for the same reason: a
// group dial view is several things at once and re-fetching them piecemeal is
// how they get out of step.

const fPath = (groupId: number) => `/groups/${groupId}/fasting`;

export function fetchFasting(groupId: number): Promise<FastingState> {
  return api<FastingState>(fPath(groupId));
}

export function startFast(groupId: number, body: StartFastRequest = {}): Promise<FastingState> {
  return api<FastingState>(`${fPath(groupId)}/sessions`, { method: 'POST', body });
}

export function updateFast(
  groupId: number,
  sessionId: number,
  body: { startedAt?: string; targetHours?: number | null },
): Promise<FastingState> {
  return api<FastingState>(`${fPath(groupId)}/sessions/${sessionId}`, { method: 'PATCH', body });
}

export function endFast(
  groupId: number,
  sessionId: number,
  body: EndFastRequest,
): Promise<FastingState> {
  return api<FastingState>(`${fPath(groupId)}/sessions/${sessionId}/end`, {
    method: 'POST',
    body,
  });
}

export function logKetosis(groupId: number, body: LogKetosisRequest): Promise<FastingState> {
  return api<FastingState>(`${fPath(groupId)}/ketosis`, { method: 'POST', body });
}

/**
 * "Am I fasting anywhere?" -- the one fasting read that is NOT group-scoped.
 *
 * Every caller asks without knowing a group id: the home indicator, the event
 * page's control, the header. See `MyFastingState` in shared for why it is a
 * single nullable row rather than a list.
 */
export function fetchMyFasting(): Promise<MyFastingState> {
  return api<MyFastingState>('/me/fasting');
}

/**
 * End the running fast from outside its group.
 *
 * ⚠ The escape hatch for the fasting lockout, not a convenience. A member
 * removed from a group mid-fast keeps the `fast_sessions` row -- it is their
 * record -- but every `/groups/:groupId/fasting` route requires MEMBER, so the
 * normal end path is refused and a new fast anywhere 409s against the one they
 * cannot reach. This route authorizes on OWNING THE ROW, which is the only
 * thing still true in that state.
 */
export function endMyFast(body: EndFastRequest): Promise<MyFastingState> {
  return api<MyFastingState>('/me/fasting/end', { method: 'POST', body });
}


export function fetchTournament(eventId: number): Promise<TournamentState> {
  return api<TournamentState>(tPath(eventId));
}

export function createTournament(
  eventId: number,
  body: CreateTournamentRequest = {},
): Promise<TournamentState> {
  return api<TournamentState>(tPath(eventId), { method: 'POST', body });
}

/** Pull in everyone who has checked in. Safe to call repeatedly. */
export function syncTournamentRoster(eventId: number): Promise<TournamentState> {
  return api<TournamentState>(`${tPath(eventId)}/roster/sync`, { method: 'POST' });
}

/** A player with no account: seatable, never notifiable. */
export function addTournamentPlayer(
  eventId: number,
  body: AddTournamentPlayerRequest,
): Promise<TournamentState> {
  return api<TournamentState>(`${tPath(eventId)}/players`, { method: 'POST', body });
}

/** Withdraw, not delete — a played round keeps naming who was at each table. */
export function withdrawTournamentPlayer(
  eventId: number,
  playerId: number,
): Promise<TournamentState> {
  return api<TournamentState>(`${tPath(eventId)}/players/${playerId}`, { method: 'DELETE' });
}

export function startTournamentRound(eventId: number): Promise<TournamentState> {
  return api<TournamentState>(`${tPath(eventId)}/rounds`, { method: 'POST' });
}

/** Replaces the round's seating wholesale. An empty array clears it. */
export function setTournamentSeating(
  eventId: number,
  roundNo: number,
  body: SetSeatingRequest,
): Promise<TournamentState> {
  return api<TournamentState>(`${tPath(eventId)}/rounds/${roundNo}/seats`, {
    method: 'PUT',
    body,
  });
}

/**
 * Draw a round automatically. Phase 2.
 *
 * The one tournament call that does not return a bare `TournamentState`: the
 * quality of a draw is transient information about that draw, and the envelope's
 * `meta` is discarded by this client.
 */
export function drawTournamentRound(
  eventId: number,
  roundNo: number,
): Promise<TournamentDrawResult> {
  return api<TournamentDrawResult>(`${tPath(eventId)}/rounds/${roundNo}/draw`, {
    method: 'POST',
  });
}

/**
 * Enter or correct scores. Phase 4.
 *
 * PARTIAL by design -- sending three players' scores updates three seats and
 * leaves the rest alone, because an organizer types one table at a time.
 */
export function setTournamentScores(
  eventId: number,
  roundNo: number,
  body: SetScoresRequest,
): Promise<TournamentState> {
  return api<TournamentState>(`${tPath(eventId)}/rounds/${roundNo}/scores`, {
    method: 'PUT',
    body,
  });
}

export function closeTournamentRound(
  eventId: number,
  roundNo: number,
): Promise<TournamentState> {
  return api<TournamentState>(`${tPath(eventId)}/rounds/${roundNo}/close`, { method: 'POST' });
}

export function reopenTournamentRound(
  eventId: number,
  roundNo: number,
): Promise<TournamentState> {
  return api<TournamentState>(`${tPath(eventId)}/rounds/${roundNo}/reopen`, { method: 'POST' });
}

/** The table-total check: the one setting the mahjong variant decides. */
export function setTournamentScoring(
  eventId: number,
  body: SetScoringRequest,
): Promise<TournamentState> {
  return api<TournamentState>(`${tPath(eventId)}/scoring`, { method: 'PUT', body });
}

/**
 * End it. `winnerPlayerId` is accepted ONLY to break a tie at the top -- the
 * winner is otherwise read from the standings, never sent.
 */
export function completeTournament(
  eventId: number,
  body: CompleteTournamentRequest = {},
): Promise<TournamentState> {
  return api<TournamentState>(`${tPath(eventId)}/complete`, { method: 'POST', body });
}

export function savePushSubscription(
  body: PushSubscribeRequest,
): Promise<{ subscribed: boolean; devices: number }> {
  return api('/me/push-subscriptions', { method: 'POST', body });
}

/** Ask the server to push a test notification to every device the caller has. */
export function sendPushTest(): Promise<{ devices: number; sent: number }> {
  return api('/me/push-test', { method: 'POST' });
}

/**
 * The endpoint travels in the body, not the path: a push endpoint is a long URL
 * and encoding one into a path segment is a reliable source of proxy trouble.
 */
export function removePushSubscription(
  endpoint: string,
): Promise<{ subscribed: boolean; devices: number }> {
  return api('/me/push-subscriptions/remove', { method: 'POST', body: { endpoint } });
}

// --- Public token actions (no login) --------------------------------------
//
// These use `raw` with skipAuth: the whole point of a signed link is that it works
// from a phone with no session, and attaching a stale Authorization header would
// invite a pointless refresh attempt on a page the visitor may not be logged into.

/** Tap-to-confirm a day-of RSVP from a signed link. */
export function confirmByToken(token: string): Promise<TokenConfirmResponse> {
  return raw<TokenConfirmResponse>('/rsvp/confirm-token', {
    method: 'POST',
    body: { token },
    skipAuth: true,
  });
}

export function unsubscribeByToken(token: string): Promise<UnsubscribeResponse> {
  return raw<UnsubscribeResponse>('/unsubscribe', {
    method: 'POST',
    body: { token },
    skipAuth: true,
  });
}

/**
 * The no-login preferences page, behind a signed `email_prefs` token.
 *
 * `skipAuth` on all three: the whole point is that they work on the device the
 * email was opened on, with no session. The token is the authorization.
 */
export function emailPrefsByToken(token: string): Promise<EmailPrefsResponse> {
  return raw<EmailPrefsResponse>('/email-prefs', { method: 'POST', body: { token }, skipAuth: true });
}

export function updateEmailPrefsByToken(
  req: EmailPrefsUpdateRequest,
): Promise<NotificationPrefs> {
  return raw<NotificationPrefs>('/email-prefs', { method: 'PATCH', body: req, skipAuth: true });
}

export function setEmailSubscriptionByToken(
  req: EmailPrefsSubscriptionRequest,
): Promise<EmailPrefsResponse> {
  return raw<EmailPrefsResponse>('/email-prefs/subscription', {
    method: 'POST',
    body: req,
    skipAuth: true,
  });
}

export function resubscribeByToken(token: string): Promise<UnsubscribeResponse> {
  return raw<UnsubscribeResponse>('/resubscribe', {
    method: 'POST',
    body: { token },
    skipAuth: true,
  });
}

// --- Event chat -----------------------------------------------------------

/** One page of messages. `before` is a message id cursor, not an offset. */
export function fetchChat(eventId: number, before?: number): Promise<ChatPage> {
  const qs = before === undefined ? '' : `?before=${before}`;
  return api<ChatPage>(`/events/${eventId}/chat${qs}`);
}

export function postChatMessage(
  eventId: number,
  body: string,
  important = false,
): Promise<ChatMessage> {
  return api<ChatMessage>(`/events/${eventId}/chat`, {
    method: 'POST',
    body: important ? { body, important: true } : { body },
  });
}

/** Mute or unmute one conversation for the caller. Separate from RSVP. */
export function setChatMute(eventId: number, muted: boolean): Promise<ChatConversationState> {
  return api<ChatConversationState>(`/events/${eventId}/chat/mute`, {
    method: 'PUT',
    body: { muted },
  });
}

/** Organizers: close or reopen the conversation. */
export function setChatClosed(eventId: number, closed: boolean): Promise<ChatConversationState> {
  return api<ChatConversationState>(`/events/${eventId}/chat/closed`, {
    method: 'PUT',
    body: { closed },
  });
}

/** Copy one message to the whole group's feed, as a post by the caller. */
export function shareChatMessage(messageId: number): Promise<FeedPost> {
  return api<FeedPost>(`/chat/${messageId}/share`, { method: 'POST' });
}

export function deleteChatMessage(messageId: number): Promise<DeleteChatResponse> {
  return api<DeleteChatResponse>(`/chat/${messageId}`, { method: 'DELETE' });
}

/** Clears the unread badge. Called on open and on receiving while open. */
export function markChatRead(eventId: number): Promise<{ eventId: number; unread: number }> {
  return api(`/events/${eventId}/chat/read`, { method: 'POST' });
}

// --- Group feed -----------------------------------------------------------

/**
 * The home page's cross-group stream. `kinds` is any of 'posts' and 'chat';
 * omitted means both.
 */
export function fetchActivity(opts: {
  before?: string | null;
  kinds?: ('posts' | 'chat')[];
} = {}): Promise<ActivityPage> {
  const q = new URLSearchParams();
  if (opts.before) q.set('before', opts.before);
  if (opts.kinds) q.set('kinds', opts.kinds.join(','));
  const qs = q.toString();
  return api<ActivityPage>(`/me/activity${qs ? `?${qs}` : ''}`);
}

// --- The group's royal flush pot -------------------------------------------

export function fetchPot(groupId: number): Promise<GroupPot> {
  return api<GroupPot>(`/groups/${groupId}/pot`);
}

export function setPotAmount(groupId: number, amountCents: number): Promise<GroupPot> {
  return api<GroupPot>(`/groups/${groupId}/pot`, { method: 'PUT', body: { amountCents } });
}

export function setPotHolder(groupId: number, userId: number | null): Promise<GroupPot> {
  return api<GroupPot>(`/groups/${groupId}/pot/holder`, { method: 'PUT', body: { userId } });
}

/** Announce a royal flush: pays the pot out, restarts it, posts to the group. */
export function payRoyalFlushPot(groupId: number, req: PotWinRequest): Promise<PotWinResponse> {
  return api<PotWinResponse>(`/groups/${groupId}/pot/win`, { method: 'POST', body: req });
}

export function fetchFeed(groupId: number, before?: number): Promise<FeedPage> {
  const qs = before === undefined ? '' : `?before=${before}`;
  return api<FeedPage>(`/groups/${groupId}/posts${qs}`);
}

/** Text-only. A post WITH a photo goes through uploadPhoto()'s `body` field. */
export function postToFeed(groupId: number, body: string): Promise<FeedPost> {
  return api<FeedPost>(`/groups/${groupId}/posts`, { method: 'POST', body: { body } });
}

/** Upsert the caller's reaction — always replaces any prior one. */
export function putReaction(postId: number, reaction: PutReactionRequest['reaction']): Promise<ReactionActionResponse> {
  return api<ReactionActionResponse>(`/posts/${postId}/reaction`, { method: 'PUT', body: { reaction } });
}

/** Remove the caller's own reaction. */
export function removeReaction(postId: number): Promise<ReactionActionResponse> {
  return api<ReactionActionResponse>(`/posts/${postId}/reaction`, { method: 'DELETE' });
}

/** The same two calls against a chat message. Gated on chat access, not membership. */
export function putChatReaction(
  messageId: number,
  reaction: PutReactionRequest['reaction'],
): Promise<ChatReactionActionResponse> {
  return api<ChatReactionActionResponse>(`/chat/${messageId}/reaction`, {
    method: 'PUT',
    body: { reaction },
  });
}

export function removeChatReaction(messageId: number): Promise<ChatReactionActionResponse> {
  return api<ChatReactionActionResponse>(`/chat/${messageId}/reaction`, { method: 'DELETE' });
}

export function deleteFeedPost(postId: number): Promise<{ postId: number; deleted: boolean }> {
  return api(`/posts/${postId}`, { method: 'DELETE' });
}

/**
 * Report objectionable content. App Store Review Guideline 1.2.
 *
 * Answers 404 for content the caller cannot see, exactly as it does for content
 * that does not exist — the endpoint is deliberately not an oracle for probing
 * ids across other people's groups.
 */
export function reportContent(body: ReportRequest): Promise<ReportResponse> {
  return api<ReportResponse>('/reports', { method: 'POST', body });
}

// --- Site admin -----------------------------------------------------------
//
// All of these 403 for a non-admin. The nav hides the Admin link on siteRole,
// but that is cosmetic — the server is the authority and the view handles a 403
// rather than assuming it can never happen.

/** The code table and the scope selector's options, in one round trip. */
export function adminFetchInviteCodes(): Promise<AdminInviteCodesResponse> {
  return api<AdminInviteCodesResponse>('/admin/invite-codes');
}

export function adminCreateInviteCode(body: AdminCreateInviteCodeRequest): Promise<InviteCode> {
  return api<InviteCode>('/admin/invite-codes', { method: 'POST', body });
}

/** Revoke or restore. Returns the row with its status re-derived by the server. */
export function adminSetInviteCodeActive(
  codeId: number,
  isActive: boolean,
): Promise<AdminInviteCode> {
  return api<AdminInviteCode>(`/admin/invite-codes/${codeId}`, {
    method: 'PATCH',
    body: { isActive },
  });
}

/**
 * The whole-site group list, for the admin Groups section.
 *
 * `order` is sent to the server rather than sorted here: this is every group on
 * the site, and once it is paged, sorting the page the client happens to hold
 * would silently mean something different from sorting the list.
 */
export function adminListGroups(options: {
  q?: string;
  status?: AdminGroupStatus;
  order?: AdminGroupOrder;
} = {}): Promise<AdminGroupRow[]> {
  const qs = new URLSearchParams();
  if (options.q) qs.set('q', options.q);
  if (options.status) qs.set('status', options.status);
  if (options.order) qs.set('order', options.order);
  const suffix = qs.toString();
  return api<AdminGroupRow[]>(`/admin/groups${suffix ? `?${suffix}` : ''}`);
}

/**
 * Suspend, restore, delete, or undelete one group. Returns the re-read row, so
 * the caller never has to guess what the change actually did.
 */
export function adminUpdateGroup(
  groupId: number,
  changes: { isSuspended?: boolean; isDeleted?: boolean },
): Promise<AdminGroupRow> {
  return api<AdminGroupRow>(`/admin/groups/${groupId}`, { method: 'PATCH', body: changes });
}

/** Every account, for the admin Users section. */
export function adminListUsers(options: {
  q?: string;
  status?: AdminUserStatus;
  order?: AdminUserOrder;
} = {}): Promise<AdminUserRow[]> {
  const qs = new URLSearchParams();
  if (options.q) qs.set('q', options.q);
  if (options.status) qs.set('status', options.status);
  if (options.order) qs.set('order', options.order);
  const suffix = qs.toString();
  return api<AdminUserRow[]>(`/admin/users${suffix ? `?${suffix}` : ''}`);
}

/**
 * Suspend, restore, or unlock one account. Returns the re-read row.
 *
 * `unlock: true` is an action rather than `isLocked: false`, because it has to
 * clear the manual hold AND the automatic lockout's counter and timestamp.
 */
export function adminUpdateUser(
  userId: number,
  changes: AdminUpdateUserRequest,
): Promise<AdminUserRow> {
  return api<AdminUserRow>(`/admin/users/${userId}`, { method: 'PATCH', body: changes });
}

// --- Masquerade -----------------------------------------------------------
//
// Every one of these swaps the ACCESS token and leaves the refresh token alone.
// The refresh token is the admin's own and is never re-issued for the
// impersonated user — a durable credential for somebody else is the one thing
// this feature must never create.

/** Begin acting as another user, read-only. Write mode is a separate step. */
export async function startMasquerade(userId: number): Promise<MasqueradeState> {
  const data = await api<AuthResponse & { masquerade: MasqueradeState }>(
    `/admin/masquerade/${userId}`,
    { method: 'POST' },
  );
  setAccessToken(data.accessToken, data.expiresIn);
  setMasquerade(data.masquerade);
  // The effective user just changed. Acting as somebody in another zone has to
  // show THEIR zone, or the admin reads every time on the screen wrongly while
  // believing they are seeing what that user sees.
  forgetViewerZone();
  return data.masquerade;
}

/**
 * Switch an in-flight masquerade between read-only and write mode.
 *
 * Deliberately does NOT forget the viewer zone, unlike every other token swap
 * here: the subject is the same user before and after, so their zone cannot
 * have changed. Dropping the cache would only cost a needless /me round trip.
 */
export async function setMasqueradeMode(mode: MasqueradeMode): Promise<MasqueradeState> {
  const data = await api<AuthResponse & { masquerade: MasqueradeState }>(
    '/auth/masquerade/write',
    { method: 'POST', body: { mode } },
  );
  setAccessToken(data.accessToken, data.expiresIn);
  setMasquerade(data.masquerade);
  return data.masquerade;
}

/** Stop, and go back to being the admin. */
export async function endMasquerade(): Promise<CurrentUser> {
  const data = await api<AuthResponse>('/auth/masquerade/end', { method: 'POST' });
  setAccessToken(data.accessToken, data.expiresIn);
  setMasquerade(null);
  // Back to being the admin, whose zone is not the subject's.
  forgetViewerZone();
  return data.user;
}

/**
 * Register a native device token (APNs / FCM).
 *
 * `senderAvailable` comes back false today: the token is stored but nothing sends
 * to it yet. The UI must not claim push is working on the strength of a successful
 * registration.
 */
export function registerDeviceToken(
  token: string,
  platform: 'ios' | 'android',
): Promise<{ registered: boolean; platform: string; senderAvailable: boolean }> {
  return api('/me/device-tokens', { method: 'POST', body: { token, platform } });
}

// --- Address book ----------------------------------------------------------

export interface ListContactsOptions {
  search?: string;
  ties?: SocialTie[];
  lists?: number[];
  states?: string[];
  countries?: string[];
  sort?: ContactSortKey;
  dir?: SortDirection;
  limit?: number;
  offset?: number;
}

export function listContacts(opts: ListContactsOptions = {}): Promise<ContactsPageDto> {
  const q = new URLSearchParams();
  if (opts.search) q.set('search', opts.search);
  if (opts.ties?.length) q.set('ties', opts.ties.join(','));
  if (opts.lists?.length) q.set('lists', opts.lists.join(','));
  if (opts.states?.length) q.set('states', opts.states.join(','));
  if (opts.countries?.length) q.set('countries', opts.countries.join(','));
  if (opts.sort) q.set('sort', opts.sort);
  // Only when descending: ascending is the server default, and an explicit
  // `dir=asc` on every request makes the common URL noisier for nothing.
  if (opts.dir === 'desc') q.set('dir', 'desc');
  if (opts.limit !== undefined) q.set('limit', String(opts.limit));
  if (opts.offset !== undefined) q.set('offset', String(opts.offset));
  const qs = q.toString();
  return api<ContactsPageDto>(`/contacts${qs ? `?${qs}` : ''}`);
}

export function contactFacets(): Promise<ContactFacetsDto> {
  return api<ContactFacetsDto>('/contacts/facets');
}

// --- Households ------------------------------------------------------------

export function listHouseholds(): Promise<{ households: HouseholdDto[] }> {
  return api<{ households: HouseholdDto[] }>('/households');
}

export function createHousehold(
  name: string,
  contactIds: number[],
): Promise<{ householdId: number; moved: number }> {
  return api('/households', { method: 'POST', body: { name, contactIds } });
}

export function renameHousehold(householdId: number, name: string): Promise<unknown> {
  return api(`/households/${householdId}`, { method: 'PATCH', body: { name } });
}

/** Dissolves the household. The members are RELEASED, never deleted. */
export function deleteHousehold(householdId: number): Promise<unknown> {
  return api(`/households/${householdId}`, { method: 'DELETE' });
}

export function setHouseholdMembers(
  householdId: number | null,
  contactIds: number[],
): Promise<BulkResultDto> {
  return api<BulkResultDto>('/households/members', {
    method: 'PUT',
    body: { householdId, contactIds },
  });
}

// --- Bulk actions ----------------------------------------------------------
//
// Each returns { affected, requested }. They differ when a selection has gone
// stale, and the UI reports the difference rather than hiding it.

export function bulkSetTies(
  contactIds: number[],
  ties: SocialTie[],
  mode: BulkTieMode,
): Promise<BulkResultDto> {
  return api<BulkResultDto>('/contacts/bulk/ties', {
    method: 'POST',
    body: { contactIds, ties, mode },
  });
}

export function bulkSetList(
  contactIds: number[],
  listId: number,
  member: boolean,
): Promise<BulkResultDto> {
  return api<BulkResultDto>('/contacts/bulk/list', {
    method: 'POST',
    body: { contactIds, listId, member },
  });
}

// --- Connections -----------------------------------------------------------
//
// The mutual tie between two ACCOUNTS, and the consent record the sync story
// rests on. Distinct from a social TIE, which is a private label you put on a
// contact and which needs nobody's agreement.

export function listConnections(): Promise<ConnectionsDto> {
  return api<ConnectionsDto>('/connections');
}

/**
 * Ask a contact to connect. Refused with NOT_FOUND when that contact holds no
 * account -- which is not a data error, it is most contacts, so the caller
 * offers an invite rather than reporting a failure.
 */
export function requestConnection(contactId: number): Promise<{ connectionId: number }> {
  return api('/connections', { method: 'POST', body: { contactId } });
}

/**
 * Accept an INCOMING request. Refused with 403 for the person who sent it --
 * only the other party may accept, which is what makes the connection a
 * consent record rather than something one side can grant itself.
 */
export function acceptConnection(connectionId: number): Promise<{ connectionId: number }> {
  return api(`/connections/${connectionId}/accept`, { method: 'POST' });
}

/**
 * Decline a pending request, withdraw one you sent, or revoke an accepted
 * connection. One endpoint for all three: which it is depends on the row's
 * current status and who is asking, and the server answers with the verb it
 * actually applied (`declined` or `revoked`).
 *
 * Contacts stay LINKED either way. The manual layer is the owner's own data and
 * must survive; disclosure keys off the connection status, so ending one stops
 * the sharing immediately without anybody losing what they typed.
 */
export function endConnection(
  connectionId: number,
): Promise<{ connectionId: number; status: 'declined' | 'revoked' }> {
  return api(`/connections/${connectionId}`, { method: 'DELETE' });
}

export function bulkDeleteContacts(contactIds: number[]): Promise<BulkResultDto> {
  return api<BulkResultDto>('/contacts/bulk/delete', {
    method: 'POST',
    body: { contactIds },
  });
}

export function getContact(contactId: number): Promise<ContactDetailDto> {
  return api<ContactDetailDto>(`/contacts/${contactId}`);
}

export interface WriteContactBody {
  fields?: ContactLayer;
  notes?: string | null;
  ties?: SocialTie[];
}

export function createContact(body: WriteContactBody): Promise<ContactDetailDto> {
  return api<ContactDetailDto>('/contacts', { method: 'POST', body });
}

export function updateContact(
  contactId: number,
  body: WriteContactBody,
): Promise<ContactDetailDto> {
  return api<ContactDetailDto>(`/contacts/${contactId}`, { method: 'PATCH', body });
}

export function deleteContact(contactId: number): Promise<{ contactId: number }> {
  return api<{ contactId: number }>(`/contacts/${contactId}`, { method: 'DELETE' });
}

export function listContactLists(): Promise<{ lists: ContactListDto[] }> {
  return api<{ lists: ContactListDto[] }>('/contact-lists');
}

export function createContactList(name: string): Promise<ContactListDto> {
  return api<ContactListDto>('/contact-lists', { method: 'POST', body: { name } });
}

export function setListMembership(
  listId: number,
  contactId: number,
  member: boolean,
): Promise<unknown> {
  return api(`/contact-lists/${listId}/members/${contactId}`, {
    method: 'PUT',
    body: { member },
  });
}

export interface ExportPreview {
  total: number;
  postable: number;
  missing: number;
}

export function exportPreview(lists: number[]): Promise<ExportPreview> {
  const q = new URLSearchParams();
  if (lists.length) q.set('lists', lists.join(','));
  const qs = q.toString();
  return api<ExportPreview>(`/contacts/export/preview${qs ? `?${qs}` : ''}`);
}

/**
 * Fetch the CSV and hand it to the browser as a download.
 *
 * It has to go through fetch rather than a plain link, because the API is
 * bearer-token authenticated and a link carries no Authorization header.
 * `api()` is JSON-only, so this uses the raw path with the token attached and
 * reads the body as a blob so the BOM survives — decoding it as text would
 * strip the very byte that makes Excel read UTF-8.
 */
export async function downloadContactsCsv(opts: {
  lists?: number[];
  address?: 'home' | 'work';
  scope?: 'mailing' | 'full';
}): Promise<void> {
  const q = new URLSearchParams();
  if (opts.lists?.length) q.set('lists', opts.lists.join(','));
  if (opts.address) q.set('address', opts.address);
  if (opts.scope === 'full') q.set('scope', 'full');

  // Mirror api()'s refresh-first behaviour: a stale access token would 401 and
  // the user would see a failed download with no explanation.
  if (!hasFreshAccessToken() && getRefreshToken()) await refreshSession();
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(base() + `/contacts/export/csv?${q.toString()}`, { headers });
  } catch {
    throw new ApiClientError(0, 'NETWORK', 'Cannot reach the server');
  }
  if (!res.ok) throw new ApiClientError(res.status, 'BAD_REQUEST', 'Could not build that export.');

  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? 'contacts.csv';

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel the download in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// --- Fasting history and sharing -------------------------------------------

export function fetchFastHistory(groupId: number): Promise<FastHistory> {
  return api<FastHistory>(`${fPath(groupId)}/history`);
}

export function fetchMemberFastHistory(groupId: number, userId: number): Promise<FastMemberHistory> {
  return api<FastMemberHistory>(`${fPath(groupId)}/history/members/${userId}`);
}

export function setFastShared(groupId: number, sessionId: number, shared: boolean): Promise<FastHistory> {
  return api<FastHistory>(`${fPath(groupId)}/sessions/${sessionId}/share`, {
    method: 'PUT',
    body: { shared },
  });
}
